import "./BotAnswerFeedback.less";
import thumbsUpOutlined from "@Images/thumbs-up-outline.svg";
import thumbsUpFilled from "@Images/thumbs-up-filled.svg";
import thumbsDownOutlined from "@Images/thumbs-down-outline.svg";
import thumbsDownFilled from "@Images/thumbs-down-filled.svg";

import React, { useEffect, useState } from "react";
import { AddMessageFeedbackRequest } from "@Src/hubs/chat/dtos/AddMessageFeedbackRequest";
import { useAppSelector } from "@Components/CustomerApp/hooks";
import { BotAnswerFeedbackData } from "@Models/chatBot/BotAnswerFeedbackData";
import { Tooltip } from "antd";

interface BotAnswerFeedbackProps {
    botAnswerFeedbackData: BotAnswerFeedbackData;
    onFeedbackChange: (isLike: boolean, isDislike: boolean) => void;
}

const BotAnswerFeedback: React.FC<BotAnswerFeedbackProps> = (props) => {
    const chatBotApi = useAppSelector((state) => state.customerApp.chatBotApi);
    const chat = useAppSelector((state) => state.customerApp.chat);
    const [isLiked, setIsLiked] = useState(false);
    const [isDisliked, setIsDisliked] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const { messageId, qnaNumber, knowledgeBaseId } =
        props.botAnswerFeedbackData;

    useEffect(() => {
        if (isInitialLoad && chat?.dboChatId) {
            chatBotApi
                .getMessageFeedback({
                    chatId: chat?.dboChatId,
                    messageId: messageId,
                })
                .then((messageFeedback) => {
                    if (messageFeedback != null) {
                        setIsLiked(messageFeedback);
                        setIsDisliked(!messageFeedback);
                    }
                })
                .finally(() => {
                    setIsInitialLoad(false);
                });
        }
    }, [chatBotApi, isInitialLoad, messageId, chat?.dboChatId]);

    const handleReactionToggle = (isLike: boolean): void => {
        const isNewLike = isLike ? !isLiked : false;
        const isNewDislike = isLike ? false : !isDisliked;

        setIsLiked(isNewLike);
        setIsDisliked(isNewDislike);

        if (chat?.dboChatId) {
            if (isNewLike || isNewDislike) {
                const addFeedbackRequest: AddMessageFeedbackRequest = {
                    messageId,
                    chatId: chat.dboChatId,
                    qnaNumber,
                    knowledgeBaseId,
                    wasHelpful: isNewLike, // true if like, false if dislike
                    dateRated: new Date(),
                };
                chatBotApi.addMessageFeedback(addFeedbackRequest);
            } else {
                // If both are false, remove feedback
                chatBotApi.removeMessageFeedback({
                    chatId: chat.dboChatId,
                    messageId,
                });
            }

            props.onFeedbackChange(isNewLike, isNewDislike);
        }
    };

    return (
        <div className="bot-answer-feedback">
            <div className="bot-answer-feedback__button-container">
                {!isDisliked && (
                    <Tooltip title="Helpful" trigger={["hover", "focus"]}>
                        <button
                            onClick={(): void => handleReactionToggle(true)}
                            data-testid="feedback-helpful-button"
                            className="bot-answer-feedback-button"
                            aria-label="Helpful"
                        >
                            <img
                                src={
                                    isLiked ? thumbsUpFilled : thumbsUpOutlined
                                }
                                alt="Thumbs up"
                            />
                        </button>
                    </Tooltip>
                )}
                {!isLiked && (
                    <Tooltip title="Not Helpful" trigger={["hover", "focus"]}>
                        <button
                            onClick={(): void => handleReactionToggle(false)}
                            data-testid="feedback-not-helpful-button"
                            className="bot-answer-feedback-button"
                            aria-label="Not helpful"
                        >
                            <img
                                src={
                                    isDisliked
                                        ? thumbsDownFilled
                                        : thumbsDownOutlined
                                }
                                alt="Thumbs down"
                            />
                        </button>
                    </Tooltip>
                )}
            </div>
            <div className="bot-answer-feedback__message-container">
                <span data-testid="bot-answer-feedback-message">
                    {isLiked || isDisliked
                        ? `Thanks for the feedback${isLiked ? "!" : "."}`
                        : "Was this answer helpful? Help us improve."}
                </span>
            </div>
        </div>
    );
};

export default BotAnswerFeedback;
